import exifr from 'exifr';

export type BackgroundSelection =
    | { type: 'blur' }
    | { type: 'color'; color: string };

export type ImageAnalysisResult = {
    dominantColors: string[];
    defaultBackground: BackgroundSelection;
    edgeTransparencyRatio: number;
    uniformEdgeColor?: string;
};

export type PreparedImage = {
    src: string;
    width: number;
    height: number;
    analysis: ImageAnalysisResult;
    objectUrl?: string;
};

const COLOR_BUCKET_SIZE = 30;
const ANALYSIS_MAX_SIZE = 256;

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const quantizeChannel = (value: number) =>
    clamp(Math.round(value / COLOR_BUCKET_SIZE) * COLOR_BUCKET_SIZE, 0, 255);

const colorBucketKey = (r: number, g: number, b: number) =>
    `${quantizeChannel(r)},${quantizeChannel(g)},${quantizeChannel(b)}`;

const bucketToHex = (bucket: string) => {
    const [r, g, b] = bucket.split(',').map((value) => Number(value));
    const toHex = (value: number) => value.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Image failed to load'));
        img.src = src;
    });

const canvasToBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
    });

const drawOrientedImage = (img: HTMLImageElement, orientation: number) => {
    const swapDimensions = orientation >= 5 && orientation <= 8;
    const canvas = document.createElement('canvas');
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    canvas.width = swapDimensions ? height : width;
    canvas.height = swapDimensions ? width : height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }

    switch (orientation) {
        case 2:
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            break;
        case 3:
            ctx.translate(canvas.width, canvas.height);
            ctx.rotate(Math.PI);
            break;
        case 4:
            ctx.translate(0, canvas.height);
            ctx.scale(1, -1);
            break;
        case 5:
            ctx.rotate(0.5 * Math.PI);
            ctx.scale(1, -1);
            break;
        case 6:
            ctx.translate(canvas.width, 0);
            ctx.rotate(0.5 * Math.PI);
            break;
        case 7:
            ctx.translate(canvas.width, canvas.height);
            ctx.rotate(0.5 * Math.PI);
            ctx.scale(-1, 1);
            break;
        case 8:
            ctx.translate(0, canvas.height);
            ctx.rotate(-0.5 * Math.PI);
            break;
        default:
            break;
    }

    ctx.drawImage(img, 0, 0);
    return canvas;
};

const analyzeEdges = (
    data: Uint8ClampedArray,
    width: number,
    height: number,
) => {
    const edgeCounts = new Map<string, number>();
    let transparent = 0;
    let total = 0;

    const countPixel = (idx: number) => {
        const alpha = data[idx + 3];
        total += 1;
        if (alpha < 128) {
            transparent += 1;
            return;
        }
        const key = colorBucketKey(data[idx], data[idx + 1], data[idx + 2]);
        edgeCounts.set(key, (edgeCounts.get(key) || 0) + 1);
    };

    for (let x = 0; x < width; x += 1) {
        countPixel((0 * width + x) * 4);
        if (height > 1) {
            countPixel(((height - 1) * width + x) * 4);
        }
    }
    for (let y = 1; y < height - 1; y += 1) {
        countPixel((y * width + 0) * 4);
        if (width > 1) {
            countPixel((y * width + (width - 1)) * 4);
        }
    }

    let uniformColor: string | undefined;
    let uniformCount = 0;
    for (const [key, count] of edgeCounts) {
        if (count > uniformCount) {
            uniformCount = count;
            uniformColor = key;
        }
    }

    return {
        transparentRatio: total > 0 ? transparent / total : 0,
        uniformColor: uniformColor ? bucketToHex(uniformColor) : undefined,
        uniformRatio: total > 0 ? uniformCount / total : 0,
    };
};

const extractDominantColors = (
    data: Uint8ClampedArray,
    width: number,
    height: number,
) => {
    const colorCounts = new Map<string, number>();
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const alpha = data[idx + 3];
            if (alpha < 128) {
                continue;
            }
            const key = colorBucketKey(data[idx], data[idx + 1], data[idx + 2]);
            colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
        }
    }

    const colors = Array.from(colorCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2)
        .map(([key]) => bucketToHex(key));

    if (colors.length < 2) {
        const fallback = ['#ffffff', '#000000'];
        const unique = Array.from(new Set([...colors, ...fallback]));
        return unique.slice(0, 2);
    }

    return colors;
};

const addPrimaryWhite = (colors: string[]) => {
    const white = '#ffffff';
    if (colors[0] === white) {
        return colors;
    }
    const next = [white, ...colors.filter((color) => color !== white)];
    return next.slice(0, 3);
};

const analyzeImage = async (
    img: HTMLImageElement,
): Promise<ImageAnalysisResult> => {
    const imageWidth = img.naturalWidth || img.width || 1;
    const imageHeight = img.naturalHeight || img.height || 1;
    const scale = Math.min(
        1,
        ANALYSIS_MAX_SIZE / Math.max(imageWidth, imageHeight),
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(imageWidth * scale));
    canvas.height = Math.max(1, Math.round(imageHeight * scale));

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return {
            dominantColors: [],
            defaultBackground: { type: 'blur' },
            edgeTransparencyRatio: 0,
        };
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    let imageData: ImageData;
    try {
        imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch {
        return {
            dominantColors: [],
            defaultBackground: { type: 'blur' },
            edgeTransparencyRatio: 0,
        };
    }

    const { data, width, height } = imageData;
    const edgeAnalysis = analyzeEdges(data, width, height);
    let dominantColors = extractDominantColors(data, width, height);
    let defaultBackground: BackgroundSelection = { type: 'blur' };

    if (edgeAnalysis.transparentRatio > 0.1) {
        dominantColors = addPrimaryWhite(dominantColors);
        defaultBackground = { type: 'color', color: '#ffffff' };
    } else if (edgeAnalysis.uniformRatio > 0.5 && edgeAnalysis.uniformColor) {
        defaultBackground = {
            type: 'color',
            color: edgeAnalysis.uniformColor,
        };
    }

    return {
        dominantColors,
        defaultBackground,
        edgeTransparencyRatio: edgeAnalysis.transparentRatio,
        uniformEdgeColor: edgeAnalysis.uniformColor,
    };
};

const getExifOrientation = async (blob: Blob): Promise<number> => {
    try {
        const orientation = await exifr.orientation(blob);
        return typeof orientation === 'number' ? orientation : 1;
    } catch {
        return 1;
    }
};

export const prepareImageForEditor = async (
    imageUrl: string,
): Promise<PreparedImage | null> => {
    if (!imageUrl) {
        return null;
    }

    let sourceUrl = imageUrl;
    let objectUrl: string | undefined;
    let img: HTMLImageElement;

    try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const orientation = await getExifOrientation(blob);
        const blobUrl = URL.createObjectURL(blob);

        img = await loadImage(blobUrl);
        sourceUrl = blobUrl;
        objectUrl = blobUrl;

        if (orientation !== 1) {
            const orientedCanvas = drawOrientedImage(img, orientation);
            const orientedBlob = orientedCanvas
                ? await canvasToBlob(orientedCanvas)
                : null;
            if (orientedBlob) {
                const orientedUrl = URL.createObjectURL(orientedBlob);
                URL.revokeObjectURL(blobUrl);
                sourceUrl = orientedUrl;
                objectUrl = orientedUrl;
                img = await loadImage(orientedUrl);
            }
        }
    } catch {
        try {
            img = await loadImage(imageUrl);
        } catch {
            return null;
        }
    }

    const analysis = await analyzeImage(img);

    return {
        src: sourceUrl,
        width: img.naturalWidth || img.width || 1,
        height: img.naturalHeight || img.height || 1,
        analysis,
        objectUrl,
    };
};
